/**
 * 管理员权限管理相关 Store
 * 负责权限数据的加载、管理和操作
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCacheStore } from '../cache'
import { adminPermissionsApi } from '@/https'
import type { PaginationData } from '@/https/request'
import type {
    Permission,
    PermissionListRequest,
    PermissionModuleStats,
    PermissionTreeNode,
    PermissionUpdateRequest,
    PermissionConstraintSummary
} from '@/https/types_admin/permissions'

export const useAdminPermissionsStore = defineStore('admin-permissions', () => {
    const cacheStore = useCacheStore()

    // ===== 状态管理 =====
    const permissionList = ref<Permission[]>([])
    const currentPermissionDetail = ref<Permission | null>(null)
    const permissionModules = ref<string[]>([])

    // 分页信息
    const permissionPagination = ref({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    // 查询参数
    const lastPermissionQuery = ref<PermissionListRequest>({})

    // 加载状态
    const permissionListLoading = ref(false)
    const permissionDetailLoading = ref(false)
    const permissionOperationLoading = ref(false)
    const modulesLoading = ref(false)

    // 错误状态
    const permissionListError = ref<string | null>(null)
    const permissionDetailError = ref<string | null>(null)
    const permissionOperationError = ref<string | null>(null)

    // ===== 计算属性 =====

    // 按模块分组的权限
    const permissionsByModule = computed(() => {
        const groups: Record<string, Permission[]> = {}
        permissionList.value.forEach(permission => {
            const module = permission.module || 'other'
            if (!groups[module]) {
                groups[module] = []
            }
            groups[module].push(permission)
        })
        return groups
    })

    // 权限模块统计
    const permissionModuleStats = computed((): PermissionModuleStats[] => {
        const stats: PermissionModuleStats[] = []
        Object.entries(permissionsByModule.value).forEach(([module, permissions]) => {
            stats.push({
                module,
                count: permissions.length,
                permissions
            })
        })
        return stats.sort((a, b) => b.count - a.count)
    })

    // 权限树形结构（用于选择器）
    const permissionTree = computed((): PermissionTreeNode[] => {
        const tree: PermissionTreeNode[] = []
        Object.entries(permissionsByModule.value).forEach(([module, permissions]) => {
            tree.push({
                key: module,
                title: `${module} (${permissions.length})`,
                checkable: false,
                selectable: false,
                children: permissions.map(permission => ({
                    key: `permission-${permission.id}`,
                    title: `${permission.name} (${permission.code})`,
                    checkable: true,
                    selectable: true,
                    permissions: [permission]
                }))
            })
        })
        return tree
    })

    // 权限总数
    const totalPermissions = computed(() => permissionList.value.length)

    // ===== 权限列表操作 =====

    /**
     * 加载可用的权限模块列表
     */
    const loadPermissionModules = async (forceRefresh = false) => {
        const cacheKey = 'admin-permissions-modules'

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                permissionModules.value = cached as string[]
                return permissionModules.value
            }
        }

        modulesLoading.value = true
        try {
            const modules = await adminPermissionsApi.getPermissionModules()
            permissionModules.value = Array.isArray(modules) ? modules : []
            // 缓存10分钟
            cacheStore.setCache(cacheKey, permissionModules.value, 10 * 60 * 1000)
            return permissionModules.value
        } finally {
            modulesLoading.value = false
        }
    }

    /**
     * 加载权限列表
     * @param params.scope - 'all' 获取所有权限，'assignable' 获取可分配权限（默认，排除超管专用）
     */
    const loadPermissionList = async (params: PermissionListRequest = {}, forceRefresh = false) => {
        const cacheKey = `admin-permissions-list-${JSON.stringify(params)}`

        // 检查缓存
        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                const data = cached as PaginationData<Permission>
                permissionList.value = data.items
                permissionPagination.value = {
                    total: data.total,
                    page: data.page,
                    size: data.size,
                    has_more: data.has_more
                }
                return data
            }
        }

        permissionListLoading.value = true
        permissionListError.value = null

        try {
            const response = await adminPermissionsApi.getPermissionList(params)

            permissionList.value = response.items
            permissionPagination.value = {
                total: response.total,
                page: response.page,
                size: response.size,
                has_more: response.has_more
            }
            // 不再用列表数据覆盖模块列表，模块来源于专门接口
            lastPermissionQuery.value = params

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 5 * 60 * 1000) // 5分钟缓存

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '加载权限列表失败'
            permissionListError.value = message
            throw err
        } finally {
            permissionListLoading.value = false
        }
    }

    /**
     * 编辑权限（仅超管）
     */
    const updatePermission = async (id: number, data: PermissionUpdateRequest) => {
        permissionOperationLoading.value = true
        permissionOperationError.value = null

        try {
            const response = await adminPermissionsApi.updatePermission(id, data)

            // 更新本地列表
            const index = permissionList.value.findIndex(p => p.id === id)
            if (index !== -1) {
                permissionList.value[index] = response
            }

            // 清除相关缓存
            clearPermissionCache()

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '编辑权限失败'
            permissionOperationError.value = message
            throw err
        } finally {
            permissionOperationLoading.value = false
        }
    }

    /**
     * 获取权限约束摘要
     */
    const loadConstraintSummary = async (forceRefresh = false) => {
        const cacheKey = 'admin-permissions-constraint-summary'

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                return cached as PermissionConstraintSummary
            }
        }

        try {
            const response = await adminPermissionsApi.getConstraintSummary()
            // 缓存结果
            cacheStore.setCache(cacheKey, response, 10 * 60 * 1000) // 10分钟缓存
            return response
        } catch (err: unknown) {
            console.error('加载权限约束摘要失败:', err)
            throw err
        }
    }

    /**
     * 搜索权限
     */
    const searchPermissions = async (keyword: string) => {
        return loadPermissionList({ search: keyword, page: 1 }, true)
    }

    /**
     * 按模块筛选权限
     */
    const filterPermissionsByModule = async (module: string) => {
        return loadPermissionList({ module, page: 1 }, true)
    }

    /**
     * 清除权限相关缓存
     */
    const clearPermissionCache = () => {
        cacheStore.removeCacheByPrefix('admin-permissions-')
    }

    /**
     * 重置状态
     */
    const resetPermissionState = () => {
        permissionList.value = []
        currentPermissionDetail.value = null
        permissionModules.value = []
        permissionPagination.value = {
            total: 0,
            page: 1,
            size: 20,
            has_more: false
        }
        lastPermissionQuery.value = {}

        // 重置错误状态
        permissionListError.value = null
        permissionDetailError.value = null
        permissionOperationError.value = null

        // 重置加载状态
        permissionListLoading.value = false
        permissionDetailLoading.value = false
        permissionOperationLoading.value = false
    }

    // ===== 工具方法 =====

    /**
     * 根据ID查找权限
     */
    const findPermissionById = (id: number): Permission | undefined => {
        return permissionList.value.find(p => p.id === id)
    }

    /**
     * 根据代码查找权限
     */
    const findPermissionByCode = (code: string): Permission | undefined => {
        return permissionList.value.find(p => p.code === code)
    }

    return {
        // 状态
        permissionList,
        currentPermissionDetail,
        permissionModules,
        permissionPagination,
        lastPermissionQuery,

        // 加载状态
        permissionListLoading,
        permissionDetailLoading,
        permissionOperationLoading,
        modulesLoading,

        // 错误状态
        permissionListError,
        permissionDetailError,
        permissionOperationError,

        // 计算属性
        permissionsByModule,
        permissionModuleStats,
        permissionTree,
        totalPermissions,

        // 方法
        loadPermissionModules,
        loadPermissionList,
        updatePermission,
        loadConstraintSummary,
        searchPermissions,
        filterPermissionsByModule,
        clearPermissionCache,
        resetPermissionState,
        findPermissionById,
        findPermissionByCode
    }
})